import { HStack, Stack } from "@chakra-ui/react"
import { Button, Text } from "@/components"

// Changing the bank account while a payment is waiting for approval cancels that payment
// and sends it again for approval to the new account (Bill, 2026-10-13). The Payment
// Center asks first (GET account-change-check); the payments server refuses the change
// without this confirmation (409 CONFIRM_CANCEL_PENDING). While a payment is approved or
// on its way to the bank the change is not allowed at all (409 PAYMENT_IN_PROCESS) until
// it is released, or Mike cancels it.

export const formatChangeAmount = (amount: number, currency?: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", minimumFractionDigits: 2 }).format(amount)

export const ACCOUNT_IN_PROCESS_TEXT =
  "A payment to your current account is already being processed, so your bank account can't be changed right now. " +
  "You can change it once that payment is released, or contact your equity partner if it needs to be cancelled."

const RESUBMITTED_TEXT = "Your payment that was waiting for approval has been sent again for approval and will be paid to this account."
const HELD_AFTER_RESUBMIT_TEXT = "Your new payment will be submitted after that one is approved."

// The success line after a change that touched a payment, or null when it touched none.
// `saved` is the form's own first sentence ("Your bank account has been saved.").
export const accountChangeSuccessText = (
  saved: string,
  result: { cancelled_transaction?: string; payment_held?: { message: string } } | null | undefined,
): string | null => {
  if (!result) return null
  if (result.cancelled_transaction) {
    return [saved, RESUBMITTED_TEXT, result.payment_held ? HELD_AFTER_RESUBMIT_TEXT : ""].filter(Boolean).join(" ")
  }
  if (result.payment_held?.message) return result.payment_held.message
  return null
}

interface Props {
  waiting: { amount: number; currency?: string }
  busy?: boolean
  onContinue: () => void
  onCancel: () => void
}

export const AccountChangeConfirm = ({ waiting, busy, onContinue, onCancel }: Props) => (
  // gray text, not orange: this theme's orange scale is inverted (see PaymentCenterComponent).
  <Stack p={4} borderWidth="1px" borderRadius="md" borderColor="orange.200" bg="orange.50" gap={3}>
    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
      Change Your Bank Account?
    </Text>
    <Text fontSize="sm" color="gray.700">
      You have a payment of {formatChangeAmount(waiting.amount, waiting.currency)} waiting for approval. If you change
      your bank account, that payment will be cancelled and sent again for approval to your new account.
    </Text>
    <HStack>
      <Button size="sm" variant="solid" onClick={onContinue} loading={busy} disabled={busy}>
        Continue
      </Button>
      {/* Not "Cancel" (read next to "will be cancelled", it sounds like cancelling the
          payment), and not "Keep My Current Account": Change Bank Account mode already has
          "Never mind, keep my current account", which leaves that mode; this only goes back. */}
      <Button size="sm" variant="outline" onClick={onCancel} disabled={busy}>
        Go Back
      </Button>
    </HStack>
  </Stack>
)
